import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  BaseButton,
  BaseDialog,
  BaseDivider,
  FormikField,
  BaseTextField,
  BaseTypography
} from 'src/components/common';
import { EmailTemplatesSelect } from 'src/components/fields/selects/EmailTemplatesSelect/EmailTemplatesSelect';
import { BaseDateTimePicker } from 'src/components/common/BaseDateTimePicker/BaseDateTimePicker';
import { validationErrors } from 'src/utils/validation/validationErrors';
import { validationRules } from 'src/utils/validation/validationRules';
import { IBaseForm } from 'src/utils/types/forms';
import { FormFieldsContainer } from 'src/components/common/FormFieldsGroup/FormFieldsGroup';
import { useSelector } from 'react-redux';
import {
  getDefaultEmailTemplate,
  getEmailTemplatesForSelect,
} from 'src/store/lists/emailTemplates/helpers';
import ReactPlayer from 'react-player';
import { Link, FormControl, Avatar, Grid } from '@material-ui/core';
import { RootState } from 'src/store';
import { IGuestInfo } from 'src/api/base/Guest/types';
import SunEditor, { buttonList } from 'suneditor-react';
import { BaseHelperText } from 'src/components/common/BaseTextField/BaseHelperText/BaseHelperText';
import { makeStyles } from '@material-ui/core/styles';
import noUserPicture from 'src/assets/images/no-user-picture.svg';
import { USER_ROLES } from 'src/utils/types/user';
import { BaseChipsList } from 'src/components/common/BaseChip/BaseChipsList';
import { Rating } from '@material-ui/lab';
import { IOSSwitch } from 'src/components/common/BaseButton/IosSwitch';
import LinkedInIcon from 'src/components/common/svgIcons/LinkedInIcon';
import 'src/components/common/styles/contact-guest.css';
import { MediaSection } from 'src/features/user/common/mediaViewer/MediaSection';
import ExpertiseTreeView from './ExpertiseTreeView';
import { useFetch } from 'src/utils/hooks/useFetch';
import { GuestApi } from 'src/api/base/Guest';

import phoneicon from './phone-icon.png';

export type PContactGuestModal = IBaseForm<any> & {
  open: boolean;
  onClose: () => void;
};

const useStyles = makeStyles((theme) => ({
  contentWrapper: {
    marginBottom: '1rem',
    maxWidth: '100%',
  },
  videoWrapper: {
    width: '100%',
    height: '100%',
    margin: '10px 0',
  },
  companyDescriptionWrapper: {
    border: '0.5px solid gainsboro',
    padding: '10px',
    borderRadius: '15px',
    whiteSpace: 'pre-wrap',
  },
  avatar: {
    width: 200,
    height: 200,
  },
  video: {
    marginBottom: 33,
  },
  avatarWidth: {
    width: "100px",
    height: "100px",
  },
  userData: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '10px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  userNameSection: {
    marginLeft: '30px',
  },
  userName: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '140%',
    color: theme.palette.primary.contrastText,
  },
  field: {
    [theme.breakpoints.down('sm')]: {
      marginTop: '5px',
    },
  },
  field__label: {
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '140%',
    letterSpacing: '0.03em',
    color: '#949C9E',
  },
  field__value: {
    fontWeight: 500,
    fontSize: '14px',
  },
  field__value_country: {
    fontWeight: 500,
    fontSize: '14px',
    display: 'inline-block',
  },
  field__value_language: {
    fontWeight: 500,
    fontSize: '14px',
  },
  expertises: {
    margin: '0px 0 0 -10px',
  },
  expertises__item: {
    margin: '2px 0 5px 10px',
    display: 'inline-block',
  },
  userLabel: {
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    color: "#6A7282",
    marginBottom: "3.5px",
  },
  hr: {
    width: 'calc(100% + 40px)',
    position: 'relative',
    marginLeft: '-20px',
    marginTop: '20px',
    marginBottom: '0',
    background: '#78c5f182',
    height: '1px',
    border: '0',
  },
  itemTitle: {
    padding: '12px 0 5px 0',
    fontWeight: '500'
  },
  overTitle: {
    marginBottom: '6px',
  },
  media: {
		margin: '20px 0',
	},
  tel: {
    textDecoration: 'none',
    color: '#4a4a4a',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    justifyContent: 'start',
  },
}));

const Video: React.FC<{ url: string }> = (props) => {
  const { url } = props;

  const [canPlay, setCanPlay] = useState<boolean>(ReactPlayer.canPlay(url));

  const styles = useStyles();

  if (url.includes('vimeo.com')) {
    return (
      <>
        <Link href={url} target="_blank" rel="noreferrer">
          {url}
        </Link>
        <br />
      </>
    );
  }

  return canPlay ? (
    <ReactPlayer
      onError={(_) => setCanPlay(false)}
      url={url}
      width="100%"
      height="400px"
      className={styles.video}
    />
  ) : (
    <>
      <Link href={url} target="_blank" rel="noreferrer">
        {url}
      </Link>
      <br />
    </>
  );
};

export const GuestVideoList: React.FC<{ guest: IGuestInfo | null }> = (
  props,
) => {
  const { guest } = props;
  const styles = useStyles();

  const videos = guest?.videos?.map((url) => (
    <>
      <Video url={url} />
    </>
  ));

  const available = guest?.available?.map((e) => e.name).join(', ');
  const companyLogo =
    (guest?.is_company_logo_visible) ? guest?.guest_company_logo || guest?.manager_company_logo : '';
  const companyDescription =
    (guest?.is_company_description_visible) ? guest?.guest_company_description || guest?.manager_company_description : '';

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.userData}>
        <div className="userImgAndPicture">
          <div className='image'>
            <Avatar
              variant="circle"
              className={styles.avatarWidth}
              src={guest?.avatar || noUserPicture}
              alt={guest?.first_name}
            />
			{guest?.linkedin && (
				<div className='linkedin-icon'>
					<a href={guest?.linkedin} target="_blank" rel="noopener noreferrer">
					  <LinkedInIcon />
					</a>
				</div>
			)}
          </div>
          <div className={`user-bio ${styles.userNameSection}`}>
            <div className='w-50'>
              <BaseTypography>
                <div className="user-label">Name </div>
                <div className="userValue mb-12-5">{guest?.first_name} {guest?.last_name}</div>
              </BaseTypography>
              <BaseTypography className={styles.field__value}>
                <div className="user-label">Title </div>
                <div className="userValue mb-12-5">{guest?.title}</div>
              </BaseTypography>
              {guest?.company_name && (
                <BaseTypography className={styles.field__value}>
                  <div className="user-label">Company name </div>
                  <div className="userValue mb-0">{guest?.company_name}</div>
                </BaseTypography>
              )
              }
            </div>
            <div className='w-50'>
              {guest?.gender && (
                <BaseTypography className={styles.field__value_country}>
                  <div className="user-label">Gender </div>
                  <div className="userValue mb-12-5">{guest?.gender}</div>
                </BaseTypography>
              )}
			  {guest?.country && guest?.country.length > 0 && (
                <BaseTypography className={styles.field__value}>
                  <div className="user-label">Country of residence </div>
                  <div className="userValue mb-12-5">{guest?.country.map((ct) => ct.name).join(', ')}</div>
                </BaseTypography>
              )}
              {guest?.languages && guest?.languages.length > 0 && (
                <BaseTypography className={styles.field__value_language}>
                  <div className="user-label">Languages </div>
                  <div className="userValue mb-0">{guest?.languages.map((language) => language.name).join(', ')}</div>
                </BaseTypography>
              )
              }
            </div>
          </div>
        </div>
      </div>
      {/* <hr className={styles.hr} /> */}
	  {((guest?.rating && guest?.rating !== 0) || (guest?.available && guest?.available.length > 0) || (guest?.expertise && guest?.expertise.length > 0) || guest?.description) && (
		<>
		  <BaseDivider />
		  <div className="key-information">
			Key Information
		  </div>
      {guest?.rating ? (
        <>
        {guest?.rating !== 0 && (
          <>
          <div className="key-information-title mt-25 mb-7">
            Rating  
          </div>

          <Rating
            name="rating"
            size="medium"
            value={guest?.rating || 0}
            precision={1}
            readOnly
          />
          </>
        )}
        </>
      ): (
        <>
        </>
      )}
		  {guest?.available && guest?.available.length > 0 && (
			<div>
			  <div className="key-information-title mt-25 mb-7">
				Available for appearances in
			  </div>
			  <BaseChipsList
				list={guest?.available.map((item) => item.name) || []}
			  />
			</div>
		  )}
      {guest?.profile?.work_phone && (
			<div>
			  <div className="key-information-title mt-25 mb-7">
				<a className={styles.tel} href={`tel:${guest?.profile?.work_phone}`}><img src={phoneicon} style={{ width: '20px' }} alt="Left" /> Call via verified number</a>
			  </div>
			</div>
		  )}
		  {guest?.expertise && guest?.expertise.length > 0 && guest?.selectedExpertiseIds?.some((item) => item.hierarchy_id) && (
			<div>
			  <div className="key-information-title mt-25 mb-7">
				Areas of expertise
			  </div>
			  { /* <BaseChipsList
				list={guest?.expertise.map((item) => item.name) || []}
			  /> */ }
			  <ExpertiseTreeView
				  expertiseTree={guest?.expertiseTree}
				  selectedExpertiseIds={guest?.selectedExpertiseIds}
				/>
			</div>
		  )}
		  {guest?.description && (
			<>
			  <div className="key-information-title mt-25 mb-7">Unique insights</div>
			  <div
				style={{
				  fontWeight: 400,
				  fontSize: "16px",
				  lineHeight: "24px",
          color: "#0A0A0A",
				}}>
				{guest?.description}
			  </div>
			</>
		  )}
		</>
	  )}
	  { /* guest?.training_certificate && (
		<>
			<div className="key-information-title mt-25 ">Training certificate</div>
			<a href={guest?.training_certificate} target='_blank'
				style={{
					fontWeight: 400,
					fontSize: "16px",
					lineHeight: "24px",
					color: '#4A9EDB',
					display: 'inline-block',
					textDecoration: 'none'
				}} rel="noreferrer">
				{guest?.training_certificate}
			</a>
		</>
	) */ }
	{ /* guest?.onedrive_link && (
		<>
			<div style={{ marginTop: '16px' }} className="key-information-title ">Training videos</div>
			<a href={guest?.onedrive_link} target='_blank'
				style={{
					fontWeight: 400,
					fontSize: "16px",
					lineHeight: "24px",
					color: '#4A9EDB',
					display: 'inline-block',
					textDecoration: 'none'
				}} rel="noreferrer">
				{guest?.onedrive_link}
			</a>
		</>
	) */ }
	  {(guest?.is_sharepoint) && (guest?.background_briefings_story) && (
		<>
			{guest?.background_briefings_story && (
				<>
					<div style={{ marginTop: '16px' }} className="key-information-title">Background briefing</div>
					<a href={guest?.background_briefings_story} target='_blank'
						style={{
							fontWeight: 400,
							fontSize: "16px",
							lineHeight: "24px",
							color: '#4A9EDB',
							display: 'inline-block',
							textDecoration: 'none'
						}} rel="noreferrer">
						{guest?.background_briefings_story}
					</a>
				</>
			)}

		</>
	  )}
      { /* (guest?.videos && guest?.videos.length > 0 && guest?.videos[0]) && (
        <>
          <div className="key-information-title mt-25 mb-7">Video/Text link:</div>
          <div>{videos}</div>
        </>
      ) */ }
      { /* guest?.linkedin && (
        <>
          <div className="key-information-title mt-25 mb-7">LinkedIn:</div>
          <Link target="_blank" href={guest?.linkedin}>{guest?.linkedin}</Link>
        </>
      )}
      {available && (
        <>
          <div className="key-information-title mt-25 mb-7">Media suitable for:</div>
          <div>{available}</div>
        </>
      ) */ }

      {(companyLogo || companyDescription) &&
        <>
          <BaseDivider />
          <div className="key-information">
            Company Information
          </div>
          <div className='company-flex-container'>{companyLogo && (
            <div style={{ width: "25%" }}>
              <Avatar
                className={`${styles.avatar} avatar`}
                variant="square"
                alt="Remy Sharp"
                src={String(companyLogo)}
              />
            </div>
          )}
            {companyDescription && (
              <div className="userValue mb-0">
                {companyDescription}
              </div>
            )}
          </div>

        </>}

        {(guest?.videos && guest?.videos.length > 0 && guest?.videos[0] !== "" || guest?.images && guest?.images.length > 0) && (
          <>
            <div className="key-information" style={{ marginTop: '10px' }}>
              Gallery
            </div>
            <Grid item md={12}>
              <MediaSection
                className={styles.media}
                videos={guest?.videos || []}
                images={guest?.images || []}
                comeFrom="popup"
              />
            </Grid>
          </>
        )}
      <BaseDivider />
    </div>
  );
};

export const ContactGuestModal: React.FC<PContactGuestModal> = (props) => {
  const { open, onClose, onSubmit, loading, guestId } = props;
  const defaultEmailTemplate = useSelector(getDefaultEmailTemplate);
  const user = useSelector((state: RootState) => state.auth.user);
  const { guest } = useSelector((state: RootState) => state.currentGuest);

   const [{ response, isLoading }, doFetch] = useFetch<any, any>(
      GuestApi.getGuest,
    );

   useEffect(() => {
	   if(guestId) {
			doFetch(guestId);
	   }
    }, [guestId, doFetch]);

  const formik = useFormik({
    validationSchema: yup.object().shape({
      email_template_id: yup.number().required(validationErrors.REQUIRED_FIELD),
      date_interview: validationRules.requiredDefaultStringRule,
      newTempTitle: yup.mixed().when('is_a_new_temp', {
        is: true,
        then: yup.mixed().required(validationErrors.REQUIRED_FIELD),
      }),
    }),
    initialValues: {
      email_template_id: '',
      date_interview: '',
      comment: '',
      is_a_new_temp: false,
      newTempSec: false,
      newTempTitle: '',
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  const { email_template_id } = formik.values;
  const { setFieldValue } = formik;
  useEffect(() => {
    if (defaultEmailTemplate && !email_template_id) {
      setFieldValue('email_template_id', defaultEmailTemplate.id);
    }
  }, [defaultEmailTemplate, setFieldValue, email_template_id]);
  const emailTemplates = useSelector(getEmailTemplatesForSelect);
  const templateValue = emailTemplates.find(
    (item) => item.value === email_template_id,
  );

  useEffect(() => {
    if (templateValue) {
      formik.setFieldValue('comment', templateValue.content);
    }
  }, [templateValue]);

  const styles = useStyles();

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="Invite Guest for interview"
      isFullWidthTitleDivider
      paperStyles={{ maxWidth: '830px', width: '100%', color: '#101828', }}>
      <GuestVideoList guest={response?.data?.data} />
      {/* <div style={{ marginBottom: '1rem' }}>
        <h3>Video:</h3>
        <List>
          {guest?.videos?.map((item) => {
            return (
              <ListItem>
                <Link href={item}>
                  {item.length > 40 ? `${item.slice(0, 40)}...` : item}
                </Link>
              </ListItem>
            );
          })}
          <Divider />
        </List>
        <h3>LinkedIn:</h3>
        <Link href={guest?.linkedin}>{guest?.linkedin}</Link>
        <Divider />
      </div> */}
      {(user?.role === USER_ROLES.JOURNALIST) ? (
        <form onSubmit={formik.handleSubmit}>
          <FormFieldsContainer>
            <div className='form-input-section'>

              <div className='w-70 email'>
                <FormikField
                  label={
                    <div className="input-label">
                      Invite using this email template:
                    </div>
                  }
                  name="email_template_id"
                  formik={formik}
                  inputComponent={EmailTemplatesSelect}
                  onChange={(newValue: number) => {
                    formik.setFieldValue('email_template_id', newValue);
                  }}
                />
              </div>

              <div className='w-30 interview date'>
                <BaseDateTimePicker
                  label={
                    <div className="interview input-label">
                      Invite for interview on:
                    </div>
                  }
                  value={formik.values.date_interview}
                  errorText={formik.errors.date_interview}
                  onChange={(value: any) => {
                    formik.setFieldValue('date_interview', value);
                  }}
                  disablePast
                  startPosition
                />
              </div>
            </div>
            <FormControl error={Boolean(formik.errors.comment)}>

              <div className="input-label">
                Question:
              </div>
              {/* <BaseInputLabel>Question:</BaseInputLabel> */}
              <SunEditor
                setDefaultStyle="font-size: 16px; font-family: IBM Plex Sans;"
                showToolbar
                name="comment"
                setContents={
                  templateValue ? templateValue.content : formik.values.comment
                }
                onBlur={(e, editorContent) => {
                  formik.setFieldValue('comment', editorContent);
                  formik.setFieldValue('newTempSec', true);
                }}
                setOptions={{
                  height: 200,
                  fontSize: [36],
                  buttonList: [
                    ...buttonList.formatting,
                    ['font', 'align', 'lineHeight'],
                  ],
                  // Other option
                }}
              />
              <BaseHelperText>{formik.errors.comment}</BaseHelperText>
            </FormControl>
            {/* {formik.values.newTempSec && (
              <>
                <BaseButtonGroup
                  value={formik.values.is_a_new_temp}
                  label="Do you want to save the changes as a new template?"
                  onChange={(value) => {
                    formik.setFieldValue('is_a_new_temp', value);
                  }}
                />
                {formik.values.is_a_new_temp && (
                  <>
                    <div>
                      <BaseTextField
                        fullWidth
                        value={formik.values.newTempTitle}
                        errorText={formik.errors.newTempTitle}
                        onChange={formik.handleChange}
                        name="newTempTitle"
                        label="Enter the name of the new template"
                      />
                    </div>
                  </>
                )}
              </>
            )} */}

            <div className='create-new-template'>

              <div className='w-60 label-switch'>
                <BaseTypography variant="caption" className="mt-8">
                  Do you want to save the changes as a new template?<span><IOSSwitch name="is_a_new_temp" checked={formik.values.is_a_new_temp} onChange={formik.handleChange} /></span>
                </BaseTypography>
              </div>

              <div className='w-20 invite'><BaseButton type="submit" fullWidth loading={loading}>
                Invite Guest
              </BaseButton></div>

            </div>
            {formik.values.is_a_new_temp && <div>
              <BaseTextField
                fullWidth
                value={formik.values.newTempTitle}
                errorText={formik.errors.newTempTitle}
                onChange={formik.handleChange}
                name="newTempTitle"
                label="Enter the name of the new template"
              />
            </div>}
          </FormFieldsContainer>
        </form>
      ) : (
        <></>
      )
      }
    </BaseDialog>
  );
};
